import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, MapPin, Star, ArrowRight, Pencil, Users2, Wallet, CalendarDays, Clock } from "lucide-react";
import PriceBlock from "@/components/PriceBlock";

export default function Results() {
  const [results, setResults] = useState([]);
  const [brief, setBrief] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const r = sessionStorage.getItem("tablo_results");
    const b = sessionStorage.getItem("tablo_brief");
    if (!r) {
      nav("/home");
      return;
    }
    setResults(JSON.parse(r));
    if (b) setBrief(JSON.parse(b));
  }, [nav]);

  const edit = () => nav("/home");

  return (
    <div className="space-y-6 animate-fade-up" data-testid="results-page">
      <header>
        <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Matches found</p>
        <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
          {results.length} venues for your {brief?.pax || ""}-person {brief?.vibe || "group"} in {brief?.area}.
        </h1>
        <p className="text-light-ink text-sm mt-2">Ranked by fit to your budget, vibe and area. Every spot is bookable — packages where available, à-la-carte otherwise.</p>
      </header>

      {/* Edit chips — quick way to change date/place/budget/pax without re-typing the whole brief */}
      {brief && (
        <div className="tablo-card p-3 flex items-center gap-2 flex-wrap" data-testid="results-edit-bar">
          <button onClick={edit} className="tablo-pill bg-cream border border-tablo-border text-ink inline-flex items-center gap-1" data-testid="edit-area">
            <MapPin className="w-3.5 h-3.5" strokeWidth={1.6}/> {brief.area}
          </button>
          <button onClick={edit} className="tablo-pill bg-cream border border-tablo-border text-ink inline-flex items-center gap-1" data-testid="edit-date">
            <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.6}/> {brief.date}
          </button>
          <button onClick={edit} className="tablo-pill bg-cream border border-tablo-border text-ink inline-flex items-center gap-1" data-testid="edit-time">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.6}/> {brief.time}
          </button>
          <button onClick={edit} className="tablo-pill bg-cream border border-tablo-border text-ink inline-flex items-center gap-1" data-testid="edit-pax">
            <Users2 className="w-3.5 h-3.5" strokeWidth={1.6}/> {brief.pax} pax
          </button>
          <button onClick={edit} className="tablo-pill bg-cream border border-tablo-border text-ink inline-flex items-center gap-1" data-testid="edit-budget">
            <Wallet className="w-3.5 h-3.5" strokeWidth={1.6}/> ₹{brief.budget}/head
          </button>
          <div className="ml-auto">
            <button onClick={edit} className="tablo-btn-outline text-xs inline-flex items-center gap-1.5" data-testid="results-edit-btn">
              <Pencil className="w-3.5 h-3.5" strokeWidth={1.6}/> Edit search
            </button>
          </div>
        </div>
      )}

      {results.length === 0 && (
        <div className="tablo-card p-8 text-center">
          <p className="font-serif text-xl">No venues matched.</p>
          <p className="text-light-ink text-sm mt-2">Try a wider area or a different vibe.</p>
          <button onClick={edit} className="tablo-btn-primary mt-4" data-testid="results-empty-edit">Edit search</button>
        </div>
      )}

      <div className="space-y-5">
        {results.map((row) => {
          const isPackage = !!row.package && row.suggested_mode !== "a_la_carte";
          const restUrl = row.package
            ? `/restaurant/${row.restaurant.id}?package=${row.package.id}`
            : `/restaurant/${row.restaurant.id}`;
          return (
            <div key={row.restaurant.id} className="tablo-card overflow-hidden" data-testid={`result-card-${row.restaurant.id}`}>
              <div className="aspect-[16/9] bg-cream relative">
                <img src={row.restaurant.photos?.[0] || "https://images.pexels.com/photos/19447626/pexels-photo-19447626.jpeg"} alt={row.restaurant.name} className="w-full h-full object-cover" />
                <div className="absolute bottom-3 left-3 flex gap-2">
                  {(row.restaurant.vibe_tags || []).slice(0, 2).map((t) => (
                    <span key={t} className="tablo-pill bg-ink/85 text-cream backdrop-blur">{t}</span>
                  ))}
                </div>
                <div className="absolute top-3 right-3 tablo-pill bg-cream/90 text-ink">
                  Fit {row.fit_score}/100
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-2xl">{row.restaurant.name}</h3>
                    <p className="text-sm text-light-ink flex items-center gap-1 mt-1">
                      <MapPin className="w-3.5 h-3.5" strokeWidth={1.6}/> {row.restaurant.area}
                      <span className="mx-2 text-light-ink/40">•</span>
                      <Star className="w-3.5 h-3.5 text-amber" strokeWidth={1.6}/> {row.restaurant.google_rating}
                    </p>
                  </div>
                  <div className="text-right">
                    {isPackage ? (
                      <PriceBlock
                        listPrice={row.package.list_price_per_head_inr}
                        tabloPrice={row.package.price_per_head_inr}
                        suffix="/head"
                        size="md"
                        testid={`result-price-${row.restaurant.id}`}
                      />
                    ) : (
                      <div className="text-right" data-testid={`result-alacarte-price-${row.restaurant.id}`}>
                        <p className="text-[10px] uppercase tracking-wider text-light-ink">Your budget</p>
                        <p className="font-serif text-2xl">₹{brief?.budget}</p>
                        <p className="text-[11px] text-light-ink">/head · à-la-carte</p>
                      </div>
                    )}
                  </div>
                </div>

                {row.reason && (
                  <div className="mt-4 bg-amber-soft/20 border border-amber-soft/40 rounded-xl p-3 flex gap-2 items-start" data-testid={`result-reason-${row.restaurant.id}`}>
                    <Sparkles className="w-4 h-4 text-amber mt-0.5" strokeWidth={1.6} />
                    <p className="text-sm text-ink leading-snug">{row.reason}</p>
                  </div>
                )}

                {isPackage ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(row.package.inclusions || []).slice(0, 3).map((inc) => (
                      <span key={inc} className="tablo-pill bg-cream text-ink border border-tablo-border">{inc}</span>
                    ))}
                  </div>
                ) : (
                  <div className="mt-4 flex flex-wrap gap-2" data-testid={`result-alacarte-${row.restaurant.id}`}>
                    <span className="tablo-pill bg-cream text-ink border border-tablo-border">Tablo curates within your budget</span>
                    <span className="tablo-pill bg-cream text-ink border border-tablo-border">Or pick your own menu</span>
                  </div>
                )}

                <Link
                  to={restUrl}
                  className="mt-5 tablo-btn-primary w-full inline-flex items-center justify-center gap-2"
                  data-testid={`result-view-btn-${row.restaurant.id}`}
                >
                  View & book <ArrowRight className="w-4 h-4" strokeWidth={2}/>
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-center">
        <Link to="/home" className="text-sm text-amber underline" data-testid="results-refine-link">Refine search</Link>
      </div>
    </div>
  );
}
